import type { Filter, VideoStyle, StyleFilterConfig } from '../types';

// Video Filter Definitions for Color Grading
export const ColorFilters: Record<string, Filter> = {
    // Warm tones (classic real estate look)
    warm: {
        name: 'Warm Tones',
        description: 'Classic real estate warm look',
        ffmpegFilter: 'eq=brightness=0.05:saturation=1.2,colorbalance=rs=0.1:gs=0:bs=-0.1'
    },

    // Cool/Modern
    cool: {
        name: 'Cool Modern',
        description: 'Modern, cool tones',
        ffmpegFilter: 'eq=brightness=0.02:saturation=1.1,colorbalance=rs=-0.1:gs=0:bs=0.1'
    },

    // High contrast
    highContrast: {
        name: 'High Contrast',
        description: 'Punchy, high contrast look',
        ffmpegFilter: 'eq=contrast=1.3:brightness=0.03:saturation=1.15'
    },

    // Soft/Dreamy
    soft: {
        name: 'Soft Dreamy',
        description: 'Soft, dreamy aesthetic',
        ffmpegFilter: 'eq=contrast=0.9:brightness=0.08:saturation=0.95,gblur=sigma=0.5'
    },

    // Natural/Neutral
    natural: {
        name: 'Natural',
        description: 'Natural, neutral colors',
        ffmpegFilter: 'eq=brightness=0.02:saturation=1.05'
    },

    // Vibrant
    vibrant: {
        name: 'Vibrant',
        description: 'Vibrant, saturated colors',
        ffmpegFilter: 'eq=brightness=0.05:saturation=1.4:contrast=1.1'
    },

    // Cinematic
    cinematic: {
        name: 'Cinematic',
        description: 'Film-like cinematic grade',
        ffmpegFilter: 'eq=contrast=1.2:brightness=-0.02:saturation=0.9,curves=vintage'
    },

    // Black and White
    blackAndWhite: {
        name: 'Black & White',
        description: 'Monochrome look',
        ffmpegFilter: 'hue=s=0,eq=contrast=1.2'
    }
};

// Style-specific filter mappings
export const StyleFilters: Record<VideoStyle, StyleFilterConfig> = {
    modern: {
        filter: 'vibrant',
        description: 'Vibrant, energetic colors'
    },

    elegant: {
        filter: 'warm',
        description: 'Warm, sophisticated tones'
    },

    dynamic: {
        filter: 'highContrast',
        description: 'High contrast, punchy look'
    },

    cinematic: {
        filter: 'cinematic',
        description: 'Film-like cinematic grade'
    },

    minimalist: {
        filter: 'natural',
        description: 'Clean, natural colors'
    }
};

/**
 * Get filter for a style
 */
export function getFilterForStyle(style: VideoStyle): Filter {
    const styleConfig = StyleFilters[style];
    const filterName = styleConfig.filter;
    return ColorFilters[filterName];
}

/**
 * Combine multiple filters
 */
export function combineFilters(...filterNames: string[]): string {
    const filters = filterNames
        .map(name => ColorFilters[name]?.ffmpegFilter)
        .filter(Boolean);

    return filters.join(',');
}

/**
 * Apply filter to video segment with optional adjustments
 */
export function applyFilter(
    filterName: string,
    options: {
        brightness?: number;
        contrast?: number;
        saturation?: number;
    } = {}
): string {
    const filter = ColorFilters[filterName];
    if (!filter) return '';

    let filterString = filter.ffmpegFilter;

    // Apply additional adjustments if provided
    if (options.brightness !== undefined) {
        filterString += `,eq=brightness=${options.brightness}`;
    }
    if (options.contrast !== undefined) {
        filterString += `,eq=contrast=${options.contrast}`;
    }
    if (options.saturation !== undefined) {
        filterString += `,eq=saturation=${options.saturation}`;
    }

    return filterString;
}
